/**
 * CollapsibleReport — collapsed-by-default wrapper around ReportView for the
 * quote/payment screen ("View your full report" expander).
 *
 * Owns its own expansion state AND its own useReportQuery subscription so
 * neither toggling nor report fetches ever re-render PaymentsSection (whose
 * Embedded Checkout must not remount mid-session). Rendered as a SIBLING of
 * the payment cards, never a wrapper. Absence or failure of the report
 * renders nothing: the payment UI is completely unaffected either way.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../../../common';
import { Text } from '../../../common/Typography';
import { useReportQuery } from '../../../hooks/queries/report';
import ReportView from './ReportView';

const CollapsibleReport = ({ applicationId }) => {
  const { t } = useTranslation('dashboard');
  const [expanded, setExpanded] = useState(false);
  const { data: report } = useReportQuery(applicationId);

  if (!report) return null;

  return (
    <div>
      <Card padding="none" className={expanded ? 'mb-4' : ''}>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="w-full flex items-center justify-between gap-3 px-4 py-4 sm:gap-4 sm:px-6 text-left hover:bg-gray-50 transition-colors min-h-[44px]"
        >
          <span className="flex items-center gap-3">
            <span className="h-9 w-9 bg-teal-50 rounded-full flex items-center justify-center flex-shrink-0">
              <FileText className="h-4 w-4 text-[#00796b]" />
            </span>
            <span>
              <Text className="font-medium text-gray-900">
                {expanded
                  ? t('payments.hideFullReport', 'Hide your full report')
                  : t('payments.viewFullReport', 'View your full report')}
              </Text>
              <Text className="text-sm text-gray-500">
                {t('payments.reportExpanderHint', 'The trademark report our team prepared for you')}
              </Text>
            </span>
          </span>
          {expanded
            ? <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
            : <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />}
        </button>
      </Card>
      {expanded && <ReportView applicationId={applicationId} />}
    </div>
  );
};

export default CollapsibleReport;
